// SCR-STAFF-702 — درخواست‌ها و نتایج (US-MED-040.3، 044، 050، 052، 053؛ FLOW-14): نتایج بازبینی‌نشده من (بحرانی اول)، درخواست‌های دیرکرده من
// (Urgent > ۴۸ ساعت، Routine > ۱۴ روز) و صف بخش داخلی شعبه (نمونه‌گیری، در حال انجام، ثبت نتیجه).
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { unwrap, type MedOrderStatus, type MedOrderView, type MedResultView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ListPage, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { medKeys, OrderStatusChip, SCR_STAFF_702 } from './common';
import { EnterResultDialog, ResultReviewDialog } from './ResultDialogs';

type View = 'review' | 'overdue' | 'queue';

export function OrdersResultsPage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { timeZone } = useDisplaySettings();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  const views: View[] = [
    ...(access.can('med.result.review') ? (['review'] as const) : []),
    ...(access.can('med.order.create') ? (['overdue'] as const) : []),
    ...(access.can('med.order.track') ? (['queue'] as const) : []),
  ];
  const [view, setView] = useState<View>(views[0] ?? 'queue');
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [entering, setEntering] = useState<MedOrderView | null>(null);
  const time = (value: string) => toJalaliDateTime(value, { timeZone });

  const pending = useQuery({ queryKey: medKeys.myPending(tenantId), enabled: view === 'review', queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/my/results/pending')) });
  const overdue = useQuery({ queryKey: medKeys.myOverdue(tenantId), enabled: view === 'overdue', queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/my/orders/overdue')) });
  const queue = useQuery({ queryKey: medKeys.queue(tenantId), enabled: view === 'queue', queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/orders/internal-queue')) });
  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: MedOrderStatus }) => unwrap(tenantApi.POST('/api/v1/medical-orders/orders/{id}:set-status', { params: { path: { id } }, body: { status } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: medKeys.queue(tenantId) }),
  });

  const resultColumns: DataTableColumn<MedResultView>[] = [
    { id: 'title', headerKey: 'med:result.title_field', cell: (row) => row.title ?? row.orderNumber ?? t(`med:order_type.${row.type}`) },
    {
      id: 'patient',
      headerKey: 'med:common.patient',
      cell: (row) => (
        <RouterLink to={`/patients/${row.patientId}?tab=medorders`} data-testid="med-pending-patient">
          {t('med:common.open_patient')}
        </RouterLink>
      ),
    },
    { id: 'received', headerKey: 'med:common.date', cell: (row) => time(row.receivedAt) },
    {
      id: 'flags',
      headerKey: 'med:common.status',
      cell: (row) =>
        row.hasCritical ? (
          <Chip size="small" color="error" label={t('med:result.critical_short')} data-testid="med-pending-critical" />
        ) : row.hasAbnormal ? (
          <Chip size="small" color="warning" label={t('med:result.abnormal')} />
        ) : (
          <Chip size="small" variant="outlined" label={t('med:flag.Normal')} />
        ),
    },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setReviewing(row.id)} data-testid="med-pending-review">
          {t('med:result.review')}
        </Button>
      ),
    },
  ];
  const orderColumns = (queueMode: boolean): DataTableColumn<MedOrderView>[] => [
    { id: 'number', headerKey: 'med:order.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'items', headerKey: 'med:order.items', cell: (row) => row.items.map((item) => item.name).join(t('med:common.list_separator')) },
    { id: 'priority', headerKey: 'med:order.priority', cell: (row) => (row.priority === 'Urgent' ? <Chip size="small" color="warning" label={t('med:priority.Urgent')} /> : t('med:priority.Routine')) },
    { id: 'status', headerKey: 'med:common.status', cell: (row) => <Stack direction="row" spacing={0.5}><OrderStatusChip status={row.status} />{row.overdue ? <Chip size="small" color="error" label={t('med:order.overdue')} /> : null}</Stack> },
    { id: 'date', headerKey: 'med:common.date', cell: (row) => time(row.orderedAt) },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) =>
        queueMode && full ? (
          <Stack direction="row" spacing={0.5}>
            {row.status === 'Ordered' && (
              <Button size="small" onClick={() => move.mutate({ id: row.id, status: 'SampleCollected' })} data-testid="med-queue-collect">
                {t('med:order.collect')}
              </Button>
            )}
            {row.status === 'SampleCollected' && (
              <Button size="small" onClick={() => move.mutate({ id: row.id, status: 'InProgress' })} data-testid="med-queue-progress">
                {t('med:order.in_progress')}
              </Button>
            )}
            {access.can('med.result.enter') && (
              <Button size="small" onClick={() => setEntering(row)} data-testid="med-queue-result">
                {t('med:result.enter')}
              </Button>
            )}
          </Stack>
        ) : (
          <RouterLink to={`/patients/${row.patientId}?tab=medorders`}>{t('med:common.open_patient')}</RouterLink>
        ),
    },
  ];

  return (
    <ListPage screenId={SCR_STAFF_702} titleKey="med:orders_page.title" subtitleKey="med:orders_page.subtitle">
      <Tabs value={view} onChange={(_, next: View) => setView(next)} sx={{ mb: 1.5 }} aria-label={t('med:orders_page.title')}>
        {views.map((item) => (
          <Tab key={item} value={item} label={t(`med:orders_page.${item}`)} data-testid={`med-orders-tab-${item}`} />
        ))}
      </Tabs>
      {view === 'review' && (
        <DataTable captionKey="med:orders_page.review" columns={resultColumns} rows={pending.data?.items} getRowId={(row) => row.id} isLoading={pending.isPending} error={pending.error} onRetry={() => void pending.refetch()} emptyTitleKey="med:orders_page.review_empty" />
      )}
      {view === 'overdue' && (
        <DataTable captionKey="med:orders_page.overdue" columns={orderColumns(false)} rows={overdue.data?.items} getRowId={(row) => row.id} isLoading={overdue.isPending} error={overdue.error} onRetry={() => void overdue.refetch()} emptyTitleKey="med:orders_page.overdue_empty" />
      )}
      {view === 'queue' && (
        <DataTable captionKey="med:orders_page.queue" columns={orderColumns(true)} rows={queue.data?.items} getRowId={(row) => row.id} isLoading={queue.isPending} error={queue.error} onRetry={() => void queue.refetch()} emptyTitleKey="med:orders_page.queue_empty" />
      )}
      {reviewing && (
        <ResultReviewDialog
          open
          resultId={reviewing}
          onClose={() => setReviewing(null)}
          onReviewed={() => {
            setReviewing(null);
            void queryClient.invalidateQueries({ queryKey: medKeys.myPending(tenantId) });
          }}
        />
      )}
      {entering && (
        <EnterResultDialog
          open
          patientId={entering.patientId}
          order={entering}
          onClose={() => setEntering(null)}
          onSaved={() => {
            setEntering(null);
            void queryClient.invalidateQueries({ queryKey: medKeys.queue(tenantId) });
          }}
        />
      )}
    </ListPage>
  );
}
